import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";

import {
  SupportAiModelSchema,
  type SupportAiGateway,
  type SupportAutomationOverview,
  type SupportAutomationRun,
  type SupportSettings as Settings,
} from "../../shared/support-automation";
import { ContentLoading } from "../components/ContentLoading";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { CellTitle, RowTable, type RowColumn } from "../components/ui/row-table";
import type { OperationsNavigate } from "./navigation";
import { LabeledInput, LabeledSelect } from "./support-fields";
import { getSupportSettings, supportKeys, updateSupportSettings } from "./support-api";
import { automationFailureText, formatWhen } from "./support-format";
import { supportTicketPath } from "./support-route";
import { SupportReplyTimesForm } from "./SupportReplyTimes";
import { SupportSavedReplies } from "./SupportSavedReplies";

function Part({
  id,
  title,
  description,
  children,
}: {
  id: string;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={id} className="flex flex-col gap-3">
      <div>
        <h3 id={id} className="text-lg font-bold">
          {title}
        </h3>
        <p className="mt-1 max-w-3xl text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  );
}

/** Saving one part resets only that part's form, not a half-edited neighbor. */
const aiKey = (settings: Settings) =>
  [settings.aiGateway, settings.aiModel, settings.triageEnabled, settings.draftsEnabled].join("|");

/**
 * The support desk's settings (ADR-0089): the AI automations and their gateway and model, reply
 * times, and saved replies. Nothing here sends to a customer.
 */
export function SupportSettings({ navigate }: { navigate: OperationsNavigate }) {
  const overview = useQuery({ queryKey: supportKeys.settings(), queryFn: getSupportSettings });
  const data = overview.data;
  return (
    <section aria-labelledby="support-settings-title" className="flex flex-col gap-8">
      <h2 id="support-settings-title" className="text-xl font-bold">
        Support settings
      </h2>
      {overview.isPending ? <ContentLoading label="Loading settings…" variant="cards" /> : null}
      {overview.error ? <FieldError>{overview.error.message}</FieldError> : null}
      {data ? (
        <>
          <Part
            id="support-ai-title"
            title="AI automations"
            description="The AI reads new tickets and customer replies. It never sends anything to a customer: a person reviews and sends every reply."
          >
            <SettingsForm key={aiKey(data.settings)} overview={data} />
          </Part>
          <Part
            id="support-reply-times-title"
            title="Reply times"
            description="How soon the team should reply to a waiting customer, by priority, counted in business hours."
          >
            <SupportReplyTimesForm
              key={JSON.stringify(data.settings.serviceLevels)}
              levels={data.settings.serviceLevels}
            />
          </Part>
          <Part
            id="support-saved-replies-title"
            title="Saved replies"
            description="Text the team inserts into a reply and edits before sending."
          >
            <SupportSavedReplies />
          </Part>
          <RecentRuns runs={data.runs} navigate={navigate} />
        </>
      ) : null}
    </section>
  );
}

const GATEWAY_NAMES: Record<SupportAiGateway, string> = {
  cloudflare: "Cloudflare AI Gateway",
  vercel: "Vercel AI Gateway",
};

function Automation({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-md border p-4 hover:border-primary">
      <input
        checked={checked}
        className="mt-1 size-5 shrink-0 accent-[var(--primary)]"
        onChange={(event) => {
          onChange(event.currentTarget.checked);
        }}
        type="checkbox"
      />
      <span className="min-w-0">
        <span className="block font-semibold">{label}</span>
        <span className="block text-sm text-muted-foreground">{description}</span>
      </span>
    </label>
  );
}

type DraftProps = {
  draft: Settings;
  onChange: (draft: Settings) => void;
  disabled: boolean;
};

function AutomationChoices({ draft, onChange }: DraftProps) {
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="mb-2 font-semibold">Automations</legend>
      <Automation
        label="Triage new tickets"
        description="Sets the type, priority, and tags that nobody has set yet, and adds an internal note with a short summary."
        checked={draft.triageEnabled}
        onChange={(triageEnabled) => {
          onChange({ ...draft, triageEnabled });
        }}
      />
      <Automation
        label="Draft replies"
        description="When a customer writes, saves a draft reply on the ticket for a person to review, edit, and send. A draft someone on the team started is never replaced."
        checked={draft.draftsEnabled}
        onChange={(draftsEnabled) => {
          onChange({ ...draft, draftsEnabled });
        }}
      />
    </fieldset>
  );
}

function GatewayChoices({ draft, onChange, disabled }: DraftProps) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="flex flex-col gap-1">
        <LabeledSelect
          id="support-ai-gateway"
          label="AI gateway"
          value={draft.aiGateway}
          options={(["cloudflare", "vercel"] as const).map((gateway) => ({
            value: gateway,
            label: GATEWAY_NAMES[gateway],
          }))}
          onChange={(value) => {
            onChange({ ...draft, aiGateway: value === "vercel" ? "vercel" : "cloudflare" });
          }}
          disabled={disabled}
        />
        <p className="text-sm text-muted-foreground">
          {draft.aiGateway === "cloudflare"
            ? "Billed through Cloudflare. No key needed."
            : "Uses the AI_GATEWAY_API_KEY secret on the platform Worker."}
        </p>
      </div>
      <div className="flex flex-col gap-1">
        <LabeledInput
          id="support-ai-model"
          label="Model"
          value={draft.aiModel}
          onChange={(aiModel) => {
            onChange({ ...draft, aiModel });
          }}
          disabled={disabled}
        />
        <p className="text-sm text-muted-foreground">
          The model ID as the gateway names it, for example anthropic/claude-sonnet-5.
        </p>
      </div>
    </div>
  );
}

const settingsChanged = (draft: Settings, saved: Settings) =>
  draft.aiGateway !== saved.aiGateway ||
  draft.aiModel.trim() !== saved.aiModel ||
  draft.triageEnabled !== saved.triageEnabled ||
  draft.draftsEnabled !== saved.draftsEnabled;

function SettingsForm({ overview }: { overview: SupportAutomationOverview }) {
  const queryClient = useQueryClient();
  const saved = overview.settings;
  const [draft, setDraft] = useState<Settings>(saved);
  const model = SupportAiModelSchema.safeParse(draft.aiModel);
  const save = useMutation({
    mutationFn: () =>
      updateSupportSettings({
        aiGateway: draft.aiGateway,
        aiModel: draft.aiModel.trim(),
        triageEnabled: draft.triageEnabled,
        draftsEnabled: draft.draftsEnabled,
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(supportKeys.settings(), next);
    },
  });
  const problem = save.error?.message ?? (model.success ? null : model.error.issues[0]?.message);
  return (
    <form
      aria-label="AI settings"
      className="flex flex-col gap-5 rounded-xl bg-card p-6 shadow-card"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <AutomationChoices draft={draft} onChange={setDraft} disabled={save.isPending} />
      <GatewayChoices draft={draft} onChange={setDraft} disabled={save.isPending} />
      {draft.aiGateway === "vercel" && !overview.gatewaysReady.vercel ? (
        <p role="alert" className="text-sm text-destructive">
          The Vercel AI Gateway key is not installed, so automations fail until it is.
        </p>
      ) : null}
      {problem ? <FieldError>{problem}</FieldError> : null}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="submit"
          disabled={!settingsChanged(draft, saved) || !model.success || save.isPending}
        >
          {save.isPending ? "Saving…" : "Save changes"}
        </Button>
        {saved.updatedByName ? (
          <span className="text-sm text-muted-foreground">
            Last changed by {saved.updatedByName}, {formatWhen(saved.updatedAt)}
          </span>
        ) : null}
      </div>
    </form>
  );
}

const AUTOMATION_NAMES = { triage: "Triage", draft_reply: "Draft reply" } as const;

const SKIP_REASONS: Readonly<Record<string, string>> = {
  solved: "the ticket was solved",
  person_draft: "someone on the team had a draft",
  answered: "the team had already replied",
  automatic_message: "the message was automatic",
  ticket_gone: "the ticket was deleted",
};

function RunResult({ run }: { run: SupportAutomationRun }) {
  switch (run.status) {
    case "succeeded":
      return <Badge variant="success">Done</Badge>;
    case "failed":
      return (
        <span className="flex flex-col items-start gap-1">
          <Badge variant="destructive">Failed</Badge>
          <span className="text-sm text-muted-foreground">
            {automationFailureText(run.errorCode)}
          </span>
        </span>
      );
    case "skipped":
      return (
        <span className="flex flex-col items-start gap-1">
          <Badge variant="secondary">Skipped</Badge>
          <span className="text-sm text-muted-foreground">
            {SKIP_REASONS[run.outcome ?? ""] ?? "nothing to do"}
          </span>
        </span>
      );
    case "queued":
    case "running":
      return <Badge variant="warning">Running</Badge>;
  }
}

const RUN_COLUMNS: readonly RowColumn<SupportAutomationRun>[] = [
  {
    key: "ticket",
    header: "Ticket",
    cell: (run) => (
      <CellTitle title={run.ticketReference} detail={AUTOMATION_NAMES[run.automation]} />
    ),
  },
  { key: "result", header: "Result", cell: (run) => <RunResult run={run} /> },
  {
    key: "model",
    header: "Model",
    className: "hidden md:table-cell",
    cell: (run) => <span className="text-sm">{run.model}</span>,
  },
  {
    key: "when",
    header: "When",
    cell: (run) => <span className="text-sm tabular-nums">{formatWhen(run.createdAt)}</span>,
  },
];

function RecentRuns({
  runs,
  navigate,
}: {
  runs: readonly SupportAutomationRun[];
  navigate: OperationsNavigate;
}) {
  return (
    <section aria-labelledby="support-runs-title" className="flex flex-col gap-3">
      <h3 id="support-runs-title" className="text-lg font-bold">
        Recent AI runs
      </h3>
      <RowTable
        label="Recent automation runs"
        columns={RUN_COLUMNS}
        rows={runs}
        rowKey={(run) => run.id}
        rowLabel={(run) => `Open ${run.ticketReference}`}
        onOpen={(run) => {
          navigate(supportTicketPath(run.ticketId));
        }}
        empty="No runs yet. They appear here when tickets arrive."
      />
    </section>
  );
}
